import type { PermitFeesTotals } from '@contracts/order/ReviewableOrderResponse';
import { TransactionCurrency } from '@contracts/payment/Currency';
import { roundToTwoDecimalsString } from '@contracts/utils/RoundingAmount';
import { css } from '@emotion/css';
import type { RowDefinition } from '@src/common/components/SimpleTable';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
} from '@src/common/components/Table';
import { TableRow } from '@src/common/components/TableRow';
import { theme } from '@src/common/styles/Theme';
import { useTranslation } from 'react-i18next';

interface Prop {
  totals: PermitFeesTotals;
}

export const PermitTotal = ({ totals }: Prop) => {
  const { t } = useTranslation();

  const columnsName = [
    { name: '', align: 'left' }, // ghost column for alignment
    { name: t('reviewPermit.subTotal'), align: 'left' },
    { name: t('reviewPermit.stateFees'), align: 'right' },
    { name: t('reviewPermit.serviceFees'), align: 'right' },
  ];
  const getTotal = (amount: number) =>
    t('reviewPermit.amountTotal', { amount: roundToTwoDecimalsString(amount) });

  const rows: RowDefinition[] = [
    {
      id: 'CAD-total',
      columnsContent: [
        '',
        TransactionCurrency.CAD,
        getTotal(totals[TransactionCurrency.CAD].jurisdictionFee),
        getTotal(totals[TransactionCurrency.CAD].serviceFee),
      ],
    },
    {
      id: 'USD-total',
      columnsContent: [
        '',
        TransactionCurrency.USD,
        getTotal(totals[TransactionCurrency.USD].jurisdictionFee),
        getTotal(totals[TransactionCurrency.USD].serviceFee),
      ],
    },
  ];

  return (
    <Table size="small" aria-label={t('reviewPermit.feesSubtotals')}>
      <TableHead style={{ backgroundColor: 'transparent' }}>
        <TableRow>
          {columnsName.map((column) => (
            <TableCell
              key={column.name}
              className={cellStyle(column)}
              sx={{
                borderBottom: theme.table.border.secondary,
                borderTop: theme.table.border.secondary,
              }}
            >
              {column.name}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(({ id, columnsContent }) => (
          <TableRow key={`row-${id}`} hover>
            {columnsContent.map((content, i) => (
              <TableCell
                key={`row-${id}-column-${i}`}
                className={cellStyle(columnsName[i])}
              >
                {content}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const cellStyle = ({ align }: { align: string }) => css`
  text-align: ${align};
  text-wrap: nowrap;

  &:first-of-type {
    width: 100%;
  }

  &:not(:last-child):not(:first-of-type) {
    border-right: 1px solid ${theme.palette.surface.outlineVariant};
  }
`;
